import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Assignment,
  AssignmentSubmission,
  Class,
  Student,
  Teacher,
} from "../models";
import { SUBMISSION_STATUS } from "../models/assignment-submission";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  school?: {
    id: string;
    name: string;
    isVerified: boolean;
  };
}

const processFileArray = (files: Express.Multer.File[]) =>
  files.map(file => ({
    fileName: file.filename,
    originalName: file.originalname,
    fileSize: file.size,
    mimeType: file.mimetype,
    fileUrl: `/uploads/documents/${file.filename}`,
    uploadedAt: new Date(),
  }));

class AssignmentController {
  /**
   * Create a new assignment
   * @route POST /api/v1/school/assignments
   * @access Private (School Admin, Teacher)
   */
  async createAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        semesterId,
        classId,
        title,
        description,
        totalMarks,
        deadline,
        allowLateSubmission,
        lateSubmissionPenalty,
      } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      const materialFiles = (req.files as Record<string, Express.Multer.File[]>)
        ?.materialFile;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!semesterId || !classId || !title || !totalMarks || !deadline) {
        res.status(400).json({
          success: false,
          message:
            "Semester ID, Class ID, title, total marks, and deadline are required",
          error: "Missing required fields",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: classId,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
      });

      if (!classItem) {
        res.status(400).json({
          success: false,
          message:
            "Invalid class ID or class doesn't belong to your school/semester",
          error: "Invalid class",
        } as APIResponse);
        return;
      }

      let teacherId: mongoose.Types.ObjectId;

      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
          isApproved: true,
        });

        if (!teacher) {
          res.status(403).json({
            success: false,
            message: "Teacher profile not found or not approved",
            error: "Unauthorized",
          } as APIResponse);
          return;
        }

        teacherId = teacher._id;
      } else {
        if (!classItem.teacherIds || classItem.teacherIds.length === 0) {
          res.status(400).json({
            success: false,
            message: "Class must have at least one teacher assigned",
            error: "No teacher assigned",
          } as APIResponse);
          return;
        }

        teacherId = classItem.teacherIds[0];
      }

      const materials =
        materialFiles && materialFiles.length > 0
          ? processFileArray(materialFiles)
          : [];

      const newAssignment = new Assignment({
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
        classId: mongoose.Types.ObjectId.createFromHexString(classId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        teacherId,
        title,
        description,
        totalMarks,
        deadline: new Date(deadline),
        allowLateSubmission: allowLateSubmission || false,
        lateSubmissionPenalty: lateSubmissionPenalty || 0,
        materials,
        createdBy: mongoose.Types.ObjectId.createFromHexString(userId || ""),
      });

      await newAssignment.save();

      logger.info(`Assignment created: ${newAssignment.id}`, {
        title,
        classId,
        schoolId,
        userId,
      });

      res.status(201).json({
        success: true,
        message: "Assignment created successfully",
        data: {
          assignment: {
            id: newAssignment.id,
            title: newAssignment.title,
            description: newAssignment.description,
            totalMarks: newAssignment.totalMarks,
            deadline: newAssignment.deadline,
            status: newAssignment.status,
            allowLateSubmission: newAssignment.allowLateSubmission,
            lateSubmissionPenalty: newAssignment.lateSubmissionPenalty,
            materials: newAssignment.materials,
            createdAt: newAssignment.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Error creating assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all assignments for a class or semester
   * @route GET /api/v1/school/assignments
   * @access Private (School Admin, Teacher)
   */
  async getAllAssignments(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, semesterId, status } = req.query;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const query: Record<string, unknown> = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (classId) {
        query.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (semesterId) {
        query.semesterId = new mongoose.Types.ObjectId(semesterId as string);
      }

      if (status) {
        query.status = status;
      }

      const assignments = await Assignment.find({ ...query })
        .populate("semester", "semesterName academicYear")
        .populate("class", "className classCode")
        .populate("teacher", "fullName employeeId")
        .sort({ deadline: -1 });

      const assignmentsWithStats = await Promise.all(
        assignments.map(async assignment => {
          const totalSubmissions = await AssignmentSubmission.countDocuments({
            assignmentId: assignment._id,
          });
          const gradedSubmissions = await AssignmentSubmission.countDocuments({
            assignmentId: assignment._id,
            status: SUBMISSION_STATUS.GRADED,
          });
          const lateSubmissions = await AssignmentSubmission.countDocuments({
            assignmentId: assignment._id,
            status: SUBMISSION_STATUS.LATE,
          });

          return {
            id: assignment.id,
            title: assignment.title,
            description: assignment.description,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            status: assignment.status,
            publishedAt: assignment.publishedAt,
            allowLateSubmission: assignment.allowLateSubmission,
            materials: assignment.materials,
            semester: (
              assignment as unknown as {
                semester: { semesterName: string; academicYear: string };
              }
            ).semester,
            class: (
              assignment as unknown as {
                class: { className: string; classCode: string };
              }
            ).class,
            teacher: (
              assignment as unknown as {
                teacher: { fullName: string; employeeId: string };
              }
            ).teacher,
            submissions: {
              total: totalSubmissions,
              graded: gradedSubmissions,
              late: lateSubmissions,
              pending: totalSubmissions - gradedSubmissions,
            },
            createdAt: assignment.createdAt,
          };
        }),
      );

      res.status(200).json({
        success: true,
        message: `${assignments.length} assignments found`,
        data: {
          assignments: assignmentsWithStats,
          totalCount: assignments.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching assignments:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch assignments",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get assignment by ID
   * @route GET /api/v1/school/assignments/:id
   * @access Private (School Admin, Teacher)
   */
  async getAssignmentById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("semester", "semesterName academicYear startDate endDate")
        .populate("class", "className classCode currentStudents")
        .populate("teacher", "fullName employeeId profilePicture");

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Assignment found",
        data: {
          assignment: {
            id: assignment.id,
            title: assignment.title,
            description: assignment.description,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            status: assignment.status,
            publishedAt: assignment.publishedAt,
            allowLateSubmission: assignment.allowLateSubmission,
            lateSubmissionPenalty: assignment.lateSubmissionPenalty,
            materials: assignment.materials,
            semester: (assignment as unknown as { semester: unknown }).semester,
            class: (assignment as unknown as { class: unknown }).class,
            teacher: (assignment as unknown as { teacher: unknown }).teacher,
            createdAt: assignment.createdAt,
            updatedAt: assignment.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update assignment
   * @route PUT /api/v1/school/assignments/:id
   * @access Private (School Admin, Teacher)
   */
  async updateAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const {
        title,
        description,
        totalMarks,
        deadline,
        allowLateSubmission,
        lateSubmissionPenalty,
      } = req.body;
      const schoolId = req.user?.schoolId;

      const materialFiles = (req.files as Record<string, Express.Multer.File[]>)
        ?.materialFile;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      if (assignment.status === "published") {
        const hasSubmissions = await AssignmentSubmission.exists({
          assignmentId: assignment._id,
        });

        if (hasSubmissions) {
          res.status(400).json({
            success: false,
            message:
              "Cannot update published assignment with existing submissions",
            error: "Assignment has submissions",
          } as APIResponse);
          return;
        }
      }

      if (title !== undefined) assignment.title = title;
      if (description !== undefined) assignment.description = description;
      if (totalMarks !== undefined) assignment.totalMarks = totalMarks;
      if (deadline !== undefined) assignment.deadline = new Date(deadline);
      if (allowLateSubmission !== undefined)
        assignment.allowLateSubmission = allowLateSubmission;
      if (lateSubmissionPenalty !== undefined)
        assignment.lateSubmissionPenalty = lateSubmissionPenalty;

      if (materialFiles && materialFiles.length > 0) {
        const newMaterials = processFileArray(materialFiles);
        assignment.materials = [
          ...(assignment.materials || []),
          ...newMaterials,
        ];
      }

      await assignment.save();

      logger.info(`Assignment updated: ${assignment.id}`, {
        title: assignment.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Assignment updated successfully",
        data: {
          assignment: {
            id: assignment.id,
            title: assignment.title,
            description: assignment.description,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            status: assignment.status,
            materials: assignment.materials,
            updatedAt: assignment.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error updating assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete assignment
   * @route DELETE /api/v1/school/assignments/:id
   * @access Private (School Admin, Teacher)
   */
  async deleteAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const hasSubmissions = await AssignmentSubmission.exists({
        assignmentId: new mongoose.Types.ObjectId(id),
      });

      if (hasSubmissions) {
        res.status(400).json({
          success: false,
          message: "Cannot delete assignment with existing submissions",
          error: "Assignment has submissions",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOneAndDelete({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      logger.info(`Assignment deleted: ${assignment.id}`, {
        title: assignment.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Assignment deleted successfully",
        data: {
          deletedAssignment: {
            id: assignment.id,
            title: assignment.title,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deleting assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Publish assignment
   * @route POST /api/v1/school/assignments/:id/publish
   * @access Private (School Admin, Teacher)
   */
  async publishAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      if (assignment.status === "published") {
        res.status(400).json({
          success: false,
          message: "Assignment is already published",
          error: "Already published",
        } as APIResponse);
        return;
      }

      await assignment.publish();

      logger.info(`Assignment published: ${assignment.id}`, {
        title: assignment.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Assignment published successfully",
        data: {
          assignment: {
            id: assignment.id,
            title: assignment.title,
            status: assignment.status,
            publishedAt: assignment.publishedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error publishing assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to publish assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Close assignment
   * @route POST /api/v1/school/assignments/:id/close
   * @access Private (School Admin, Teacher)
   */
  async closeAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      await assignment.close();

      logger.info(`Assignment closed: ${assignment.id}`, {
        title: assignment.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Assignment closed successfully",
        data: {
          assignment: {
            id: assignment.id,
            title: assignment.title,
            status: assignment.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error closing assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to close assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all submissions for an assignment
   * @route GET /api/v1/school/assignments/:id/submissions
   * @access Private (School Admin, Teacher)
   */
  async getAssignmentSubmissions(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { id } = req.params;
      const { status } = req.query;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      const query: Record<string, unknown> = {
        assignmentId: new mongoose.Types.ObjectId(id),
      };

      if (status) {
        query.status = status;
      }

      const submissions = await AssignmentSubmission.find({ ...query })
        .populate("student", "fullName studentId profilePicture")
        .populate("gradedBy", "fullName employeeId")
        .sort({ submittedAt: -1 });

      res.status(200).json({
        success: true,
        message: `${submissions.length} submissions found`,
        data: {
          assignment: {
            id: assignment.id,
            title: assignment.title,
            totalMarks: assignment.totalMarks,
          },
          submissions: submissions.map(submission => ({
            id: submission.id,
            student: (submission as unknown as { student: unknown }).student,
            submittedAt: submission.submittedAt,
            isLateSubmission: submission.isLateSubmission,
            marksObtained: submission.marksObtained,
            status: submission.status,
            feedback: submission.feedback,
            gradedAt: submission.gradedAt,
            gradedBy: (submission as unknown as { gradedBy: unknown }).gradedBy,
            files: submission.files,
          })),
          totalCount: submissions.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching assignment submissions:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch submissions",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Grade assignment submission
   * @route PUT /api/v1/school/assignments/:id/submissions/:submissionId/grade
   * @access Private (School Admin, Teacher)
   */
  async gradeSubmission(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id, submissionId } = req.params;
      const { marksObtained, feedback } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (
        !mongoose.Types.ObjectId.isValid(id) ||
        !mongoose.Types.ObjectId.isValid(submissionId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment or submission ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (marksObtained === undefined || typeof marksObtained !== "number") {
        res.status(400).json({
          success: false,
          message: "Marks obtained is required and must be a number",
          error: "Missing marks",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      if (marksObtained < 0 || marksObtained > assignment.totalMarks) {
        res.status(400).json({
          success: false,
          message: `Marks must be between 0 and ${assignment.totalMarks}`,
          error: "Invalid marks",
        } as APIResponse);
        return;
      }

      const submission = await AssignmentSubmission.findOne({
        _id: submissionId,
        assignmentId: new mongoose.Types.ObjectId(id),
      });

      if (!submission) {
        res.status(404).json({
          success: false,
          message: "Submission not found",
          error: "Submission not found",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(403).json({
          success: false,
          message: "Teacher profile not found",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      await submission.grade(marksObtained, feedback || "", teacher._id);

      logger.info(`Submission graded: ${submission.id}`, {
        assignmentId: id,
        marksObtained,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Submission graded successfully",
        data: {
          submission: {
            id: submission.id,
            marksObtained: submission.marksObtained,
            feedback: submission.feedback,
            status: submission.status,
            gradedAt: submission.gradedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error grading submission:", error);
      res.status(500).json({
        success: false,
        message: "Failed to grade submission",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Submit assignment (Student)
   * @route POST /api/v1/student/assignments/:id/submit
   * @access Private (Student)
   */
  async submitAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { textContent } = req.body;
      const userId = req.user?.userId;
      const files = (req.files as Record<string, Express.Multer.File[]>)
        ?.submittedFile;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        isApproved: true,
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found or not approved",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: student.schoolId,
        status: "published",
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found or not published",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      if (!assignment.canSubmit()) {
        res.status(400).json({
          success: false,
          message:
            "Assignment deadline has passed and late submissions are not allowed",
          error: "Cannot submit",
        } as APIResponse);
        return;
      }

      const existingSubmission = await AssignmentSubmission.findOne({
        assignmentId: new mongoose.Types.ObjectId(id),
        studentId: student._id,
      });

      if (existingSubmission) {
        res.status(400).json({
          success: false,
          message:
            "You have already submitted this assignment. Use the re-submit endpoint to update.",
          error: "Already submitted",
        } as APIResponse);
        return;
      }

      if (!textContent && (!files || files.length === 0)) {
        res.status(400).json({
          success: false,
          message: "Submission must include at least text content or files",
          error: "Empty submission",
        } as APIResponse);
        return;
      }

      const fileData = files ? processFileArray(files) : [];

      const submission = new AssignmentSubmission({
        assignmentId: new mongoose.Types.ObjectId(id),
        studentId: student._id,
        classId: assignment.classId,
        textContent,
        files: fileData,
      });

      await submission.save();

      try {
        // eslint-disable-next-line node/no-unsupported-features/es-syntax
        const { notificationService } = await import(
          "../services/notification-service"
        );
        await notificationService.notifyAssignmentSubmitted(
          assignment.teacherId,
          assignment.schoolId,
          student._id,
          student.fullName,
          assignment._id.toString(),
          assignment.title,
          submission._id.toString(),
          submission.isLateSubmission,
        );
      } catch (notifError) {
        logger.error("Error sending submission notification:", notifError);
      }

      logger.info(`Assignment submitted: ${assignment.id}`, {
        studentId: student.id,
        submissionId: submission.id,
      });

      res.status(201).json({
        success: true,
        message: "Assignment submitted successfully",
        data: {
          submission: {
            id: submission.id,
            submittedAt: submission.submittedAt,
            isLateSubmission: submission.isLateSubmission,
            status: submission.status,
            files: submission.files,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error submitting assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Re-submit assignment (Student)
   * @route PUT /api/v1/student/assignments/:id/resubmit
   * @access Private (Student)
   */
  async resubmitAssignment(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { textContent } = req.body;
      const userId = req.user?.userId;
      const files = (req.files as Record<string, Express.Multer.File[]>)
        ?.submittedFile;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        isApproved: true,
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found or not approved",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const assignment = await Assignment.findOne({
        _id: id,
        schoolId: student.schoolId,
        status: "published",
      });

      if (!assignment) {
        res.status(404).json({
          success: false,
          message: "Assignment not found or not published",
          error: "Assignment not found",
        } as APIResponse);
        return;
      }

      if (!assignment.canSubmit()) {
        res.status(400).json({
          success: false,
          message:
            "Assignment deadline has passed and late submissions are not allowed",
          error: "Cannot re-submit",
        } as APIResponse);
        return;
      }

      const existingSubmission = await AssignmentSubmission.findOne({
        assignmentId: new mongoose.Types.ObjectId(id),
        studentId: student._id,
      });

      if (!existingSubmission) {
        res.status(404).json({
          success: false,
          message:
            "No existing submission found. Use the submit endpoint instead.",
          error: "Submission not found",
        } as APIResponse);
        return;
      }

      if (existingSubmission.status === SUBMISSION_STATUS.GRADED) {
        res.status(400).json({
          success: false,
          message: "Cannot re-submit a graded assignment",
          error: "Already graded",
        } as APIResponse);
        return;
      }

      if (!textContent && (!files || files.length === 0)) {
        res.status(400).json({
          success: false,
          message:
            "Re-submission must include at least text content or new files",
          error: "Empty re-submission",
        } as APIResponse);
        return;
      }

      if (textContent !== undefined) {
        existingSubmission.textContent = textContent;
      }

      if (files && files.length > 0) {
        existingSubmission.files = processFileArray(files);
      }

      existingSubmission.submittedAt = new Date();

      await existingSubmission.save();

      logger.info(`Assignment re-submitted: ${assignment.id}`, {
        studentId: student.id,
        submissionId: existingSubmission.id,
      });

      res.status(200).json({
        success: true,
        message: "Assignment re-submitted successfully",
        data: {
          submission: {
            id: existingSubmission.id,
            submittedAt: existingSubmission.submittedAt,
            isLateSubmission: existingSubmission.isLateSubmission,
            status: existingSubmission.status,
            files: existingSubmission.files,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error re-submitting assignment:", error);
      res.status(500).json({
        success: false,
        message: "Failed to re-submit assignment",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student's assignments
   * @route GET /api/v1/student/assignments
   * @access Private (Student)
   */
  async getStudentAssignments(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const studentClasses = await Class.find({
        schoolId: student.schoolId,
        studentIds: student._id,
      }).select("_id");

      const classIds = studentClasses.map(c => c._id);

      const query: Record<string, unknown> = {
        classId: { $in: classIds },
        status: "published",
      };

      if (classId) {
        query.classId = new mongoose.Types.ObjectId(classId as string);
      }

      const assignments = await Assignment.find({ ...query })
        .populate("class", "className classCode")
        .populate("semester", "semesterName academicYear")
        .sort({ deadline: -1 });

      const assignmentsWithSubmission = await Promise.all(
        assignments.map(async assignment => {
          const submission = await AssignmentSubmission.findOne({
            assignmentId: assignment._id,
            studentId: student._id,
          });

          let derivedStatus = "pending";
          if (submission) {
            derivedStatus = submission.status;
          } else if (assignment.isOverdue()) {
            derivedStatus = "overdue";
          }

          return {
            id: assignment.id,
            title: assignment.title,
            description: assignment.description,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            materials: assignment.materials,
            class: (assignment as unknown as { class: unknown }).class,
            semester: (assignment as unknown as { semester: unknown }).semester,
            submission: submission
              ? {
                  id: submission.id,
                  submittedAt: submission.submittedAt,
                  isLateSubmission: submission.isLateSubmission,
                  marksObtained: submission.marksObtained,
                  status: submission.status,
                  feedback: submission.feedback,
                }
              : null,
            status: derivedStatus,
            isOverdue: assignment.isOverdue(),
            canSubmit: assignment.canSubmit() && !submission,
            canResubmit:
              assignment.canSubmit() &&
              !!submission &&
              submission.status !== SUBMISSION_STATUS.GRADED,
          };
        }),
      );

      res.status(200).json({
        success: true,
        message: `${assignments.length} assignments found`,
        data: {
          assignments: assignmentsWithSubmission,
          totalCount: assignments.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching student assignments:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch assignments",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student's submission for an assignment
   * @route GET /api/v1/student/assignments/:id/submission
   * @access Private (Student)
   */
  async getStudentSubmission(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid assignment ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const submission = await AssignmentSubmission.findOne({
        assignmentId: new mongoose.Types.ObjectId(id),
        studentId: student._id,
      })
        .populate("assignment", "title totalMarks deadline")
        .populate("gradedBy", "fullName employeeId");

      if (!submission) {
        res.status(404).json({
          success: false,
          message: "Submission not found",
          error: "No submission found for this assignment",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Submission found",
        data: {
          submission: {
            id: submission.id,
            assignment: (submission as unknown as { assignment: unknown })
              .assignment,
            submittedAt: submission.submittedAt,
            isLateSubmission: submission.isLateSubmission,
            textContent: submission.textContent,
            files: submission.files,
            marksObtained: submission.marksObtained,
            feedback: submission.feedback,
            status: submission.status,
            gradedAt: submission.gradedAt,
            gradedBy: (submission as unknown as { gradedBy: unknown }).gradedBy,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching student submission:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch submission",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

export const assignmentController = new AssignmentController();
